import { getModelFromRef, getModelFromSnapshot } from '../model';
import { PublicationIssueModel } from '../model/objects/publicationIssueModel';
import { RunModel } from '../model/objects/runModel';
import { UserNoticeModel } from '../model/objects/userNoticeModel';
import { noticeIsSubmitted } from '../notice/helpers';
import {
  EFirebaseContext,
  ENotice,
  ERef,
  ESnapshotExists,
  EOrganization,
  ESnapshot,
  exists,
  EQuery,
  FirebaseTimestamp
} from '../types';
import {
  ActiveRunStatus,
  ManuallyVerifiableRunStatus,
  OcrVerifiableRunStatus,
  Run,
  RunStatusType,
  VerifiedRunStatus,
  manuallyVerifiableRunStatuses,
  ocrVerifiableRunStatuses,
  verifiedRunStatuses
} from '../types/runs';
import { getDateStringForDateInTimezone } from '../utils/dates';
import { getErrorReporter } from '../utils/errors';
import { getOrCreatePublicationIssueForPublisher } from './publicationIssueService';
import {
  bypassAutomaticVerificationHasChanged,
  columnRunVerificationSettingsHaveChanged,
  getActiveRunStatusForNotice,
  getNoticeHasAllRelevantRuns
} from '../affidavits';
import {
  NOTICE_RECONCILE_RUNS,
  NoticeReconcileRunsEvent,
  RUN_STATUS_CHANGE,
  RunStatusChange
} from '../types/events';
import { safeAsync } from '../safeWrappers';
import { PublicationIssue } from '../types/publicationIssue';

/**
 * Every path that puts a run into AFFIDAVIT_COMPLETE logs through here, so the
 * volume entering the status and the route it took can be read off a single
 * message. `affidavitUploadedBy` separates the backend writers, which attribute
 * the upload to Column Support, from the frontend ones, which attribute it to
 * the person who uploaded. `previousStatus` also stands in for whether Column
 * was verifying the run, because only a run Column owned reaches a verifiable
 * status — true of runs created since #25327, and of the rest once the
 * historical `pending` backlog is drained.
 */
const logRunSetToAffidavitComplete = ({
  noticeSnap,
  publicationDate,
  source,
  previousStatus,
  runId
}: {
  noticeSnap: ESnapshotExists<ENotice>;
  publicationDate: string;
  source: 'run creation' | 'run reconciliation';
  previousStatus?: RunStatusType;
  runId?: string;
}) => {
  const { affidavitUploadedBy, newspaper, noticeStatus } = noticeSnap.data();
  getErrorReporter().logInfo('Run set to affidavit complete', {
    noticeId: noticeSnap.id,
    newspaperId: newspaper.id,
    noticeStatus: `${noticeStatus}`,
    publicationDate,
    source,
    ...(previousStatus ? { previousStatus } : {}),
    ...(runId ? { runId } : {}),
    ...(affidavitUploadedBy
      ? { affidavitUploadedBy: affidavitUploadedBy.id }
      : {})
  });
};

const createRun = async ({
  ctx,
  publicationIssueModel,
  notice,
  status
}: {
  ctx: EFirebaseContext;
  publicationIssueModel: PublicationIssueModel;
  notice: ERef<ENotice>;
  status: ActiveRunStatus;
}): Promise<RunModel> => {
  const newRunData: Run = {
    publicationIssue: publicationIssueModel.ref,
    publicationDate: publicationIssueModel.modelData.publicationDate,
    notice,
    status
  };
  const runRef = await ctx.runsRef().add(newRunData);
  return getModelFromRef(RunModel, ctx, runRef);
};

export const getRunForNoticeAndPublicationDate = async ({
  ctx,
  publicationDate,
  noticeSnap
}: {
  ctx: EFirebaseContext;
  publicationDate: string;
  noticeSnap: ESnapshotExists<ENotice>;
}) => {
  const runQuery = await ctx
    .runsRef()
    .where('notice', '==', noticeSnap.ref)
    .where('publicationDate', '==', publicationDate)
    .get();
  if (runQuery.size > 1) {
    throw new Error(`Duplicate runs found for notice/publicationDate pair`);
  }

  if (runQuery.empty) {
    getErrorReporter().logInfo('No run found for notice/publicationDate pair', {
      noticeId: noticeSnap.id,
      publicationDate
    });
    return null;
  }

  const runSnap = runQuery.docs[0];
  return getModelFromSnapshot(RunModel, ctx, runSnap);
};

// TODO: Remove export once confident no runs are missing at verification checkpoints
export const getOrCreateRunForNoticeAndPublicationDate = async ({
  ctx,
  publicationDate,
  noticeSnap,
  activeRunStatus
}: {
  ctx: EFirebaseContext;
  publicationDate: string;
  noticeSnap: ESnapshotExists<ENotice>;
  /**
   * Resolved from the notice's affidavit settings when omitted, which costs
   * several reads. Callers reconciling a whole notice should resolve it once
   * and pass it for every date.
   */
  activeRunStatus?: ActiveRunStatus;
}) => {
  const existingRun = await getRunForNoticeAndPublicationDate({
    ctx,
    publicationDate,
    noticeSnap
  });
  if (existingRun) {
    return existingRun;
  }

  getErrorReporter().logInfo(
    'No run exists for notice and publication date; will create one',
    {
      noticeId: noticeSnap.id,
      publicationDate
    }
  );

  // Defensive in case this function is called on a notice that doesn't yet have a newspaper
  if (!noticeSnap.data().newspaper) {
    throw new Error(
      `Cannot create run for notice ${noticeSnap.id} because it does not have a newspaper`
    );
  }

  /**
   * Because of limitations on the cron that creates publication issues (see `createPublicationIssues`),
   * and the fact that we allow users to create notices for publication dates that do not yet have publication
   * issues, we will create a new publication issue for the purposes of creating a run if needed.
   *
   * However, as the eventual plan is to expand the creation of publication issues to a date further in the future
   * and potentially tie selectable publication dates to available publication issues, we may want to revisit this
   * logic if and when those changes are made.
   */
  const publicationIssueModel = await getOrCreatePublicationIssueForPublisher(
    ctx,
    noticeSnap.data().newspaper,
    publicationDate
  );

  const status =
    activeRunStatus ??
    (await getActiveRunStatusForNotice(
      ctx,
      noticeSnap,
      await noticeSnap.data().newspaper.get()
    ));

  getErrorReporter().logInfo('Creating run for notice and publication issue', {
    noticeId: noticeSnap.id,
    newspaperId: noticeSnap.data().newspaper.id,
    noticeStatus: `${noticeSnap.data().noticeStatus}`,
    publicationDate,
    publicationIssueId: publicationIssueModel.id,
    status
  });
  if (status === RunStatusType.AFFIDAVIT_COMPLETE) {
    logRunSetToAffidavitComplete({
      noticeSnap,
      publicationDate,
      source: 'run creation'
    });
  }
  return createRun({
    ctx,
    publicationIssueModel,
    notice: noticeSnap.ref,
    status
  });
};

export const shouldReconcileRuns = async ({
  ctx,
  beforeSnap,
  afterSnap
}: {
  ctx: EFirebaseContext;
  beforeSnap: ESnapshotExists<ENotice>;
  afterSnap: ESnapshotExists<ENotice>;
}): Promise<boolean> => {
  if (!noticeIsSubmitted(afterSnap)) {
    return false;
  }

  if (!afterSnap.data().publicationDates) {
    return false;
  }

  const numberOfPublicationDatesHasChanged =
    beforeSnap.data().publicationDates?.length !==
    afterSnap.data().publicationDates.length;
  if (numberOfPublicationDatesHasChanged) {
    return true;
  }

  const noticeStatusHasChanged =
    beforeSnap.data().noticeStatus !== afterSnap.data().noticeStatus;
  if (noticeStatusHasChanged) {
    return true;
  }

  for (let i = 0; i < beforeSnap.data().publicationDates.length; i++) {
    if (
      beforeSnap.data().publicationDates[i].toMillis() !==
      afterSnap.data().publicationDates[i].toMillis()
    ) {
      return true;
    }
  }

  const runReconciliationEventQuery = await ctx
    .eventsRef<NoticeReconcileRunsEvent>()
    .where('type', '==', NOTICE_RECONCILE_RUNS)
    .where('notice', '==', afterSnap.ref)
    .where('processedAt', '==', null)
    .limit(1)
    .get();
  const existingRunReconciliationEvent = runReconciliationEventQuery.docs[0];
  if (existingRunReconciliationEvent) {
    getErrorReporter().logInfo(
      'Run reconciliation event already exists for notice; will not create a new one',
      {
        noticeId: afterSnap.id,
        existingEventId: existingRunReconciliationEvent.id
      }
    );
    return false;
  }

  const noticeModel = getModelFromSnapshot(UserNoticeModel, ctx, afterSnap);
  const activeRuns = await noticeModel.getRuns();
  const noticeHasAllRelevantRuns = await getNoticeHasAllRelevantRuns(
    afterSnap,
    activeRuns
  );
  if (!noticeHasAllRelevantRuns) {
    return true;
  }

  /**
   * Invoice creation refreshes a notice's settings from the paper, overwriting
   * notice-level overrides, so runs created skipped can become Column's
   * responsibility later, and pending runs can start or stop bypassing OCR.
   * Reconciliation is the only thing that moves them.
   */
  const settingsHaveChanged = columnRunVerificationSettingsHaveChanged(
    beforeSnap.data().affidavitReconciliationSettings,
    afterSnap.data().affidavitReconciliationSettings
  );
  if (settingsHaveChanged && activeRuns.some(run => run.isSkipped())) {
    return true;
  }

  const bypassHasChanged = bypassAutomaticVerificationHasChanged(
    beforeSnap.data().affidavitReconciliationSettings,
    afterSnap.data().affidavitReconciliationSettings
  );
  if (
    bypassHasChanged &&
    activeRuns.some(
      run =>
        run.isBypassedOcr() || run.modelData.status === RunStatusType.PENDING
    )
  ) {
    return true;
  }

  /**
   * An affidavit can arrive without a notice status change: uploading to a
   * cancelled notice deliberately leaves the status alone, and a notice that
   * reached affidavit_submitted without an affidavit keeps that status when one
   * is uploaded later.
   */
  const affidavitJustAdded =
    !beforeSnap.data().affidavit && !!afterSnap.data().affidavit;
  if (
    affidavitJustAdded &&
    activeRuns.some(run => run.isOcrVerifiable() || run.isManuallyVerifiable())
  ) {
    return true;
  }

  return false;
};

export const reconcileRunsForNotice = async ({
  ctx,
  noticeModel,
  newspaperSnap,
  options
}: {
  ctx: EFirebaseContext;
  noticeModel: UserNoticeModel;
  newspaperSnap: ESnapshot<EOrganization>;
  options?: {
    /**
     * Used to set a specific statusChangedAt timestamp for the runs - primarily for use in testing & migrations.
     */
    statusChangedAt: FirebaseTimestamp;
  };
}) => {
  getErrorReporter().logInfo('Reconciling runs for notice', {
    noticeId: noticeModel.id,
    noticeStatus: `${noticeModel.modelData.noticeStatus}`
  });

  if (!exists(newspaperSnap)) {
    getErrorReporter().logAndCaptureWarning(
      'Cannot update notice runs because newspaper does not exist',
      {
        noticeId: noticeModel.id,
        newspaperId: newspaperSnap.id
      }
    );
    return;
  }
  const timezone = newspaperSnap.data().iana_timezone;

  // Most notices will not have publication dates until they are submitted, so in most cases this check isn't necessary,
  // but that's not the case for duplicated notices, which have data (including publication dates) immediately copied to
  // the `usernotice` object prior to submission, but they don't get a status until they are submitted, so we check for that here.
  if (!noticeIsSubmitted(noticeModel)) {
    getErrorReporter().logInfo(
      'Notice is not yet submitted; will not reconcile runs',
      {
        noticeId: noticeModel.id
      }
    );
    return;
  }

  const allRunsForNotice = await noticeModel.getRuns({
    includeDisabled: true,
    includeCancelled: true,
    sortOrder: 'asc'
  });
  const publicationDateStrings = (
    noticeModel.modelData.publicationDates || []
  ).map(pubDate =>
    getDateStringForDateInTimezone({
      date: pubDate.toDate(),
      timezone
    })
  );

  // Resolving the settings cascade costs several reads, so only pay for it when
  // a run's status actually depends on the answer, and only once per notice.
  let activeRunStatus: Promise<ActiveRunStatus> | undefined;
  const getActiveRunStatus = () => {
    if (!activeRunStatus) {
      activeRunStatus = getActiveRunStatusForNotice(
        ctx,
        noticeModel,
        newspaperSnap
      );
    }
    return activeRunStatus;
  };

  const getNewStatusForActiveRun = async (
    run: RunModel
  ): Promise<RunStatusType | null> => {
    if (noticeModel.isCancelled) {
      if (run.isVerified() || run.isCancelled()) {
        return null;
      }
      return RunStatusType.CANCELLED;
    }

    /**
     * Once a notice has an affidavit nothing will verify its runs, whatever
     * attached it. Deliberately not routed through getActiveRunStatus: a run
     * in one of these statuses was already Column's to verify, and the cascade
     * would demote it to SKIPPED if the publisher has since left automated
     * affidavits.
     */
    if (
      noticeModel.modelData.affidavit &&
      (run.isOcrVerifiable() || run.isManuallyVerifiable())
    ) {
      return RunStatusType.AFFIDAVIT_COMPLETE;
    }

    if (run.isDisabled() || run.isSkipped()) {
      // Every skipped run on a qualifying notice moves together, so a notice is
      // never left with a mix that cannot satisfy haveAllRunsBeenVerified.
      const activeStatus = await getActiveRunStatus();
      return activeStatus === run.modelData.status ? null : activeStatus;
    }

    // Pending and bypassed runs only swap with each other. Like the affidavit
    // case above, a run Column already owns is not demoted to SKIPPED.
    if (run.modelData.status === RunStatusType.PENDING) {
      return (await getActiveRunStatus()) === RunStatusType.BYPASSED_OCR
        ? RunStatusType.BYPASSED_OCR
        : null;
    }

    if (run.isBypassedOcr()) {
      if ((await getActiveRunStatus()) !== RunStatusType.PENDING) {
        return null;
      }

      const today = getDateStringForDateInTimezone({
        date: new Date(),
        timezone
      });
      if (run.modelData.publicationDate < today) {
        getErrorReporter().logInfo(
          'Leaving published run in manual verification',
          {
            runId: run.id,
            noticeId: noticeModel.id,
            publicationDate: run.modelData.publicationDate
          }
        );
        return null;
      }

      return RunStatusType.PENDING;
    }

    return null;
  };
  const statusChangedAt = options?.statusChangedAt ?? ctx.timestamp();

  // For any new publication dates on the notice, create a run (if one doesn't already exist)
  // and make sure any previously disabled runs are re-enabled
  await Promise.all(
    publicationDateStrings.map(async publicationDateString => {
      let relevantRun = allRunsForNotice.find(
        run => run.modelData.publicationDate === publicationDateString
      );
      if (!relevantRun) {
        relevantRun = await getOrCreateRunForNoticeAndPublicationDate({
          ctx,
          publicationDate: publicationDateString,
          noticeSnap: noticeModel,
          activeRunStatus: await getActiveRunStatus()
        });
      }

      const newStatus = await getNewStatusForActiveRun(relevantRun);
      if (!newStatus) {
        return;
      }

      if (
        newStatus !== RunStatusType.CANCELLED &&
        (relevantRun.isOcrVerifiable() || relevantRun.isManuallyVerifiable())
      ) {
        /**
         * The status above was decided from a snapshot read before this
         * function's other awaits, so a verification landing in that gap would
         * be overwritten (ONCALL-3686). The transactional fix for that class of
         * bug was reverted in #19071 after a TRANSACTION INVALID spike, so
         * re-read immediately before the write as `recordStatus.ts` does.
         */
        await relevantRun.refreshData();
        if ((await getNewStatusForActiveRun(relevantRun)) !== newStatus) {
          getErrorReporter().logInfo(
            'Run changed while reconciling; will not update its status',
            {
              runId: relevantRun.id,
              noticeId: noticeModel.id,
              currentStatus: relevantRun.modelData.status,
              newStatus,
              publicationDate: publicationDateString
            }
          );
          return;
        }
      }

      if (newStatus === RunStatusType.AFFIDAVIT_COMPLETE) {
        logRunSetToAffidavitComplete({
          noticeSnap: noticeModel,
          publicationDate: publicationDateString,
          source: 'run reconciliation',
          previousStatus: relevantRun.modelData.status,
          runId: relevantRun.id
        });
      } else {
        getErrorReporter().logInfo(
          'Automatically updating status for active run',
          {
            runId: relevantRun.id,
            noticeId: noticeModel.id,
            currentStatus: relevantRun.modelData.status,
            newStatus,
            publicationDate: publicationDateString
          }
        );
      }

      await relevantRun.updateStatus({
        status: newStatus,
        statusChangedAt
      });
    })
  );

  // For any publication dates that are no longer on the notice, disable the runs
  // associated with those dates (if such runs exist)
  await Promise.all(
    allRunsForNotice.map(async run => {
      if (
        publicationDateStrings.includes(run.modelData.publicationDate) ||
        run.isDisabled()
      ) {
        return;
      }

      getErrorReporter().logInfo('Disabling run', {
        runId: run.id,
        noticeId: noticeModel.id,
        currentStatus: run.modelData.status,
        publicationDate: run.modelData.publicationDate
      });
      await run.updateStatus({
        status: RunStatusType.DISABLED,
        statusChangedAt
      });
    })
  );
};

export const isVerifiedRunStatus = (
  status: RunStatusType
): status is VerifiedRunStatus => {
  return verifiedRunStatuses.includes(status as VerifiedRunStatus);
};

export const isUnverifiableRunStatus = (
  status: RunStatusType
): status is typeof RunStatusType.UNVERIFIABLE => {
  return status === RunStatusType.UNVERIFIABLE;
};

export const isOcrVerifiableRunStatus = (
  status: RunStatusType
): status is OcrVerifiableRunStatus => {
  return ocrVerifiableRunStatuses.includes(status as OcrVerifiableRunStatus);
};

export const isManuallyVerifiableRunStatus = (
  status: RunStatusType
): status is ManuallyVerifiableRunStatus => {
  return manuallyVerifiableRunStatuses.includes(
    status as ManuallyVerifiableRunStatus
  );
};

export const isDisabledRunStatus = (
  status: RunStatusType
): status is typeof RunStatusType.DISABLED => {
  return status === RunStatusType.DISABLED;
};

export const isCancelledRunStatus = (
  status: RunStatusType
): status is typeof RunStatusType.CANCELLED => {
  return status === RunStatusType.CANCELLED;
};

export const isSkippedRunStatus = (
  status: RunStatusType
): status is typeof RunStatusType.SKIPPED => {
  return status === RunStatusType.SKIPPED;
};

export const isBypassedOcrRunStatus = (
  status: RunStatusType
): status is typeof RunStatusType.BYPASSED_OCR => {
  return status === RunStatusType.BYPASSED_OCR;
};

export const isAffidavitCompleteRunStatus = (
  status: RunStatusType
): status is typeof RunStatusType.AFFIDAVIT_COMPLETE => {
  return status === RunStatusType.AFFIDAVIT_COMPLETE;
};

type GetRunModelsFromQueryOptions = {
  includeDisabled: boolean;
  includeCancelled: boolean;
};

/**
 * @deprecated Use RunService.getRunModelsFromQuery instead, which is returns a `ResponseOrError`
 */
export const getRunModelsFromQuery = async (
  ctx: EFirebaseContext,
  query: EQuery<Run>,
  options: GetRunModelsFromQueryOptions
) => {
  const querySnap = await query.get();
  return querySnap.docs
    .map(runSnap => getModelFromSnapshot(RunModel, ctx, runSnap))
    .filter(runModel => {
      if (runModel.isDisabled()) {
        return options.includeDisabled;
      }

      if (runModel.isCancelled()) {
        return options.includeCancelled;
      }

      return true;
    });
};

export class RunService {
  constructor(private firebaseContext: EFirebaseContext) {}

  public getRunStatusChangeEventsQueries(runs: RunModel[]) {
    return runs.map(run =>
      this.firebaseContext
        .eventsRef<RunStatusChange>()
        .where('type', '==', RUN_STATUS_CHANGE)
        .where('ref', '==', run.ref)
        .orderBy('createdAt')
    );
  }

  public async getRunModelsFromQuery(
    query: EQuery<Run>,
    options: GetRunModelsFromQueryOptions
  ) {
    return safeAsync(() =>
      getRunModelsFromQuery(this.firebaseContext, query, options)
    )();
  }

  public async getRunsByPublicationIssueAndStatuses(
    publicationIssue: ERef<PublicationIssue>,
    statuses: RunStatusType[]
  ) {
    const query = this.firebaseContext
      .runsRef()
      .where('publicationIssue', '==', publicationIssue)
      .where('status', 'in', statuses);
    return this.getRunModelsFromQuery(query, {
      includeDisabled: true,
      includeCancelled: true
    });
  }

  // TODO: move individual functions above to methods on the Service class
}

export const __private = {
  createRun
};
